import { useEffect, useMemo } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'

import { Loader } from 'components/Loader/Loader'
import UserCardContent from 'components/UI/UserCard/UserCardContent'
import {
  StoriesAnalyticsCardContent,
  StoriesAnalyticsCardDropdown
} from 'components-ref/organisms/analytics/content/StoriesAnalyticsCard'
import { getContentStoriesAnalytics } from 'services/endpoints/analytic'
import { useAnalyticsContentCacheStore } from 'store/analyticsContentCacheStore'
import { type ContentFilter } from 'types/api/analytic'
import { type ContentModalSortFilter } from 'types/interfaces/IContentModalSortFilter'

interface StoriesAnalyticsProps {
  selectedCategory?: string
  filter?: ContentFilter
  sort: ContentModalSortFilter
}

export function StoriesAnalytics({
  selectedCategory,
  filter,
  sort
}: StoriesAnalyticsProps) {
  const { ref, inView } = useInView({})
  const { getCache, setCache } = useAnalyticsContentCacheStore()

  const normalizedCategoryId = useMemo(() => {
    return selectedCategory || undefined
  }, [selectedCategory])

  // Use primitives in query key (avoid object identity issues from `sort`)
  const filterKey = filter ?? 'all'
  const sortBy = sort?.sort_by
  const sortValue = sort?.sort_value
  const categoryKey = normalizedCategoryId ?? 'all'

  // Cache key aligned to actual params
  const cacheKey = `stories-${filterKey}-${sortBy}-${sortValue}-${categoryKey}`
  const initialCache = getCache(cacheKey)

  const queryKey = useMemo(
    () => ['storiesAnalytics', filterKey, sortBy, sortValue, categoryKey],
    [filterKey, sortBy, sortValue, categoryKey]
  )

  const adaptStoryCardForUserCard = (card: any) => {
    const photos = Array.isArray(card?.photos) ? card.photos : []
    const videos = Array.isArray(card?.videos) ? card.videos : []

    const adaptedPhotos =
      photos.length > 0
        ? photos
        : card?.image_src
          ? [
              {
                // UserCardContent's first branch uses thumb_url
                thumb_url: card.image_src,
                // other branches sometimes use url
                url: card.image_src
              }
            ]
          : []

    const adaptedVideos =
      videos.length > 0
        ? videos
        : card?.video_src
          ? [
              {
                // some parts use url
                url: card.video_src,
                // your player branch tries card.video_src (so provide it too)
                video_src: card.video_src,
                // poster usage
                active_thumb: card?.active_thumb ?? card?.image_src ?? null
              }
            ]
          : []

    return {
      ...card,
      photos: adaptedPhotos,
      videos: adaptedVideos,
      // also provide top-level video_src so url={card.video_src} works
      video_src: card?.video_src ?? null
    }
  }

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetching,
    hasNextPage
  } = useInfiniteQuery<any>(
    queryKey,
    async ({ pageParam = 1 }) =>
      await getContentStoriesAnalytics({
        categoryId: normalizedCategoryId,
        filter,
        pageParam,
        sort
      }),
    {
      //  Laravel paginator: use meta page numbers
      getNextPageParam: (lastPage) => {
        const cur = lastPage?.meta?.current_page ?? 1
        const last = lastPage?.meta?.last_page ?? 1
        return cur < last ? cur + 1 : undefined
      },

      // cache-first hydration for instant UI
      initialData: initialCache
        ? {
            pageParams: [1],
            pages: [initialCache]
          }
        : undefined,

      // still fetch once on mount even when hydrated
      refetchOnMount: 'always',

      enabled: true,
      keepPreviousData: false,

      onSuccess: (res) => {
        const firstPage = res?.pages?.[0]
        if (firstPage?.data?.length) {
          setCache(cacheKey, firstPage)
        }
      }
    }
  )

  useEffect(() => {
    if (inView && hasNextPage && !isFetching && !isLoading) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetching, isLoading, fetchNextPage])

  // If we have initialData, isLoading can be false; if not, show loader
  if (isLoading && !data) return <Loader />

  const items = data?.pages?.flatMap((page: any) => page?.data ?? []) ?? []
  const lastIndex = items.length - 1

  return (
    <>
      {items.map((rawCard: any, index: number) => {
        const card = adaptStoryCardForUserCard(rawCard)

        return (
          <span
            ref={index === lastIndex ? ref : null}
            key={card?.id ?? index}
          >
            <UserCardContent
              thumb_src={card?.image_src ?? ''}
              cardContent={<StoriesAnalyticsCardContent card={card} />}
              hasDropDown={true}
              dropDownContent={<StoriesAnalyticsCardDropdown card={card} />}
              card={card}
              customIconClass="content-icon-square"
              iconSize="80"
              previewType="story"
            />
          </span>
        )
      })}
    </>
  )
}
